import React, { useState } from 'react';
import { X, Plus, Calendar, Trash2 } from 'lucide-react';
import { useClient, useClientBodyMeasurements } from '../hooks/useClients';
import { BodyMeasurement, MeasurementKey } from '../types';
import MeasurementAdjuster from './MeasurementAdjuster';
import MeasurementsChart from './MeasurementsChart';
import { MEASUREMENT_KINDS, formatMeasurementRecord, getTrackedMeasurements } from '../utils/bodyMeasurements';

interface BodyMeasurementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
}

type MeasurementValues = Record<MeasurementKey, number>;

const DEFAULT_VALUES = Object.fromEntries(
  MEASUREMENT_KINDS.map(k => [k.key, k.defaultValue])
) as MeasurementValues;

const round1 = (value: number) => parseFloat(value.toFixed(1));

const BodyMeasurementsModal: React.FC<BodyMeasurementsModalProps> = ({ isOpen, onClose, clientId }) => {
  const { measurements, isLoading, isAddLoading, addMeasurement, deleteMeasurement, updateTracked } =
    useClientBodyMeasurements(clientId);
  const { client } = useClient(clientId);
  const [values, setValues] = useState<MeasurementValues>(DEFAULT_VALUES);
  const [date, setDate] = useState<string>(new Date().toISOString().slice(0, 10));
  // Локальная копия набора отслеживаемых замеров — меняется сразу по клику,
  // не дожидаясь ответа сервера; null — берём из карточки клиента
  const [trackedOverride, setTrackedOverride] = useState<MeasurementKey[] | null>(null);

  const tracked = trackedOverride ?? getTrackedMeasurements(client);

  // Сбрасываем локальную копию при переключении на другого клиента
  React.useEffect(() => {
    setTrackedOverride(null);
  }, [clientId]);

  // Проверяем, есть ли уже замеры за выбранную дату
  const existingMeasurementForDate = React.useMemo(() => {
    if (!measurements || !date) return null;
    const selectedDate = new Date(date);
    return measurements.find((m: BodyMeasurement) => {
      const measurementDate = new Date(m.date);
      return measurementDate.toDateString() === selectedDate.toDateString();
    });
  }, [measurements, date]);

  React.useEffect(() => {
    if (!measurements || measurements.length === 0) return;
    const next = { ...DEFAULT_VALUES };
    MEASUREMENT_KINDS.forEach(k => {
      // Замеры за выбранную дату, иначе — последнее известное значение
      // (разные замеры могли сниматься в разные дни)
      const source = existingMeasurementForDate?.[k.key] != null
        ? existingMeasurementForDate
        : measurements.find((m: BodyMeasurement) => m[k.key] != null);
      if (source && source[k.key] != null) next[k.key] = round1(source[k.key] as number);
    });
    setValues(next);
  }, [measurements, existingMeasurementForDate]);

  if (!isOpen) return null;

  const toggleTracked = async (key: MeasurementKey, checked: boolean) => {
    const previous = tracked;
    const next = MEASUREMENT_KINDS.map(k => k.key).filter(k => (k === key ? checked : previous.includes(k)));
    if (next.length === 0) return; // хотя бы один замер должен отслеживаться
    setTrackedOverride(next);
    try {
      await updateTracked(next);
    } catch {
      setTrackedOverride(previous);
    }
  };

  const handleAdd = () => {
    const valueFor = (key: MeasurementKey) => (tracked.includes(key) ? values[key] : null);
    addMeasurement({
      neck: valueFor('neck'),
      chest: valueFor('chest'),
      waist: valueFor('waist'),
      hips: valueFor('hips'),
      date: new Date(date).toISOString(),
    });
  };

  const handleDelete = (m: BodyMeasurement) => {
    const dateLabel = new Date(m.date).toLocaleDateString('ru-RU');
    if (window.confirm(`Удалить замеры за ${dateLabel}? Восстановить их будет нельзя.`)) {
      deleteMeasurement(m.id);
    }
  };

  // Последняя запись, в которой есть хоть один отслеживаемый замер
  const last: BodyMeasurement | undefined = measurements?.find((m: BodyMeasurement) =>
    tracked.some(key => m[key] != null)
  );
  const trackedKinds = MEASUREMENT_KINDS.filter(k => tracked.includes(k.key));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Замеры тела клиента</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {last ? (
                <div className="mb-4">
                  <div className="text-sm text-gray-500 mb-1">Последние замеры:</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {trackedKinds.map(k => (
                      <div key={k.key} className="flex items-center space-x-2">
                        <span className="font-medium" style={{ color: k.color }}>{k.label}:</span>
                        <span className="font-bold">{last[k.key] != null ? `${last[k.key]} см` : '—'}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center text-xs text-gray-400 mt-1">
                    <Calendar size={14} className="mr-1" />
                    {new Date(last.date).toLocaleDateString('ru-RU')}
                  </div>
                </div>
              ) : (
                <div className="mb-4 text-gray-400">Нет записей замеров</div>
              )}

              {/* График замеров */}
              {measurements && measurements.length > 0 && (
                <div className="mb-4">
                  <MeasurementsChart data={measurements} tracked={tracked} />
                </div>
              )}

              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Дата</label>
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {existingMeasurementForDate && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-700">
                      ⚠️ Замеры за эту дату уже существуют. При сохранении они будут обновлены.
                    </p>
                  </div>
                )}
              </div>

              {/* Поля замеров: галочка — замер отслеживается у клиента */}
              <div className="divide-y divide-gray-100">
                {MEASUREMENT_KINDS.map(k => {
                  const isTracked = tracked.includes(k.key);
                  return (
                    <div key={k.key} className="flex items-center justify-between py-2">
                      <label className="flex items-center space-x-3 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={isTracked}
                          onChange={e => toggleTracked(k.key, e.target.checked)}
                          disabled={isTracked && tracked.length === 1}
                          title={isTracked && tracked.length === 1 ? 'Должен отслеживаться хотя бы один замер' : undefined}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: isTracked ? k.color : '#d1d5db' }}
                        />
                        <span className={isTracked ? 'text-gray-900' : 'text-gray-400'}>{k.label}</span>
                      </label>
                      <MeasurementAdjuster
                        value={values[k.key]}
                        onChange={v => setValues(prev => ({ ...prev, [k.key]: round1(v) }))}
                        step={0.5}
                        disabled={!isTracked}
                        hideLabel
                      />
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 -mt-4">
                Снятая галочка скрывает замер с графика и из новых записей. Уже сохранённые значения не удаляются.
              </p>

              <button
                onClick={handleAdd}
                disabled={isAddLoading}
                className="w-full mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isAddLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Добавление...</span>
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    <span>Сохранить замеры</span>
                  </>
                )}
              </button>

              {/* История */}
              {measurements && measurements.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">История</h3>
                  <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                    {measurements.map((m: BodyMeasurement) => (
                      <li key={m.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div>
                          <div className="text-gray-900">{formatMeasurementRecord(m, tracked)}</div>
                          <div className="text-xs text-gray-400">{new Date(m.date).toLocaleDateString('ru-RU')}</div>
                        </div>
                        <button
                          onClick={() => handleDelete(m)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Удалить"
                        >
                          <Trash2 size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BodyMeasurementsModal;
